// src/features/contact/screens/ContactScreen.tsx
import React, { useRef, useState } from 'react';
import styles from './ContactScreen.module.scss';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import emailjs from 'emailjs-com';
import { FiMail, FiLinkedin, FiDownload, FiUser } from 'react-icons/fi';
import ShinyText from '../../../shared/components/ShinyText';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.2 },
  },
  exit: { opacity: 0 },
};

const leftColumnVariants = {
  hidden: { x: -40, opacity: 0 },
  visible: { x: 0, opacity: 1 },
};

const rightColumnVariants = {
  hidden: { x: 40, opacity: 0 },
  visible: { x: 0, opacity: 1 },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const ContactScreen: React.FC = () => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const [statusMessage, setStatusMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleDownload = (language: 'en' | 'fr') => {
    const pdfUrl = language === 'en'
      ? '/Jamil Joo – Full-Stack Software Engineer (Web & Mobile).pdf'
      : '/Jamil Joo – Ingénieur Logiciel Full-Stack (Web & Mobile).pdf';
    
    // Open PDF in new tab for viewing
    window.open(pdfUrl, '_blank');
  };

  const handleDirectEmail = () => {
    window.location.href = 'mailto:jamiljoocontact@gmail.com';
  };

  const handleLinkedIn = () => {
    window.open('https://www.linkedin.com/in/jamiljoo/', '_blank');
  };

  const handleUpwork = () => {
    window.open('https://www.upwork.com/freelancers/~011243a6d19af75131', '_blank');
  };

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    setIsSent(false);
    setIsError(false);

    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const user_name = formData.get('user_name') as string;
      const user_email = formData.get('user_email') as string;
      const message = formData.get('message') as string;

      const toUserParams = {
        user_name,
        user_email,
        message,
        current_year: new Date().getFullYear(),
      };

      // Example EmailJS usage
      emailjs
        .sendForm(
          'service_lo34zym',
          'template_y2c5n8x',
          formRef.current,
          'E9DBjCmoX4_-UeN7Y'
        )
        .then(
          () => {
            // Then auto-reply
            emailjs
              .send(
                'service_lo34zym',
                'template_d0e3buk',
                toUserParams,
                'E9DBjCmoX4_-UeN7Y'
              )
              .then(
                () => {
                  setStatusMessage(t('contact.successMessage'));
                  setIsSent(true);
                  formRef.current?.reset();
                  setIsSending(false);
                },
                (error) => {
                  setStatusMessage(t('contact.errorMessage'));
                  setIsError(true);
                  setIsSending(false);
                }
              );
          },
          (error) => {
            setStatusMessage(t('contact.errorMessage'));
            setIsError(true);
            setIsSending(false);
          }
        );
    }
  };

  return (
    <motion.div
      className={styles.contactPage}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className={styles.contactContent}>
        {/* LEFT: The Form */}
        <motion.div className={styles.leftColumn} variants={leftColumnVariants}>
          <div className={styles.formHeader}>
            <motion.h1 className={styles.pageTitle} variants={fieldVariants}>
              {t('contact.heading')}
            </motion.h1>
            <motion.p className={styles.pageSubtitle} variants={fieldVariants}>
              {t('contact.description')}
            </motion.p>
          </div>

          <motion.form
            ref={formRef}
            onSubmit={sendEmail}
            className={styles.contactForm}
          >
            <motion.div className={styles.formGroup} variants={fieldVariants}>
              <label htmlFor="name">{t('contact.form.name')}</label>
              <input type="text" id="name" name="user_name" required />
            </motion.div>

            <motion.div className={styles.formGroup} variants={fieldVariants}>
              <label htmlFor="email">{t('contact.form.email')}</label>
              <input type="email" id="email" name="user_email" required />
            </motion.div>

            <motion.div className={styles.formGroup} variants={fieldVariants}>
              <label htmlFor="message">{t('contact.form.message')}</label>
              <textarea id="message" name="message" rows={5} required></textarea>
            </motion.div>

            <motion.button
              type="submit"
              className={styles.submitButton}
              disabled={isSending}
              variants={fieldVariants}
            >
              {isSending ? <div className={styles.loader}></div> : t('contact.form.send')}
            </motion.button>

            {statusMessage && (
              <motion.p
                className={`${styles.statusMessage} ${
                  isSent ? styles.success : styles.error
                }`}
                variants={fieldVariants}
              >
                {statusMessage}
              </motion.p>
            )}
          </motion.form>
        </motion.div>

        {/* RIGHT: CTA panel */}
        <motion.div className={styles.rightColumn} variants={rightColumnVariants}>
          <h2 className={styles.methodsTitle}>{t('contact.methodsHeading')}</h2>
          <p className={styles.methodsDesc}>{t('contact.methodsIntro')}</p>

          <div className={styles.ctaList}>
            {/* Secondary CTAs */}
            <div className={styles.secondaryButtons}>
              <motion.button
                onClick={handleDirectEmail}
                className={styles.ctaButton}
              >
                <FiMail className={styles.icon} />
                {t('contact.methodEmail')}
              </motion.button>

              <motion.button
                onClick={handleLinkedIn}
                className={styles.ctaButton}
              >
                <FiLinkedin className={styles.icon} />
                {t('contact.methodLinkedIn')}
              </motion.button>
            </div>

            {/* Resume Downloads */}
            <div className={styles.resumeButtons}>
              <motion.button
                onClick={() => handleDownload('en')}
                className={styles.ctaButton}
              >
                <FiDownload className={styles.icon} />
                {t('contact.methodResumeEN')}
              </motion.button>

              <motion.button
                onClick={() => handleDownload('fr')}
                className={styles.ctaButton}
              >
                <FiDownload className={styles.icon} />
                {t('contact.methodResumeFR')}
              </motion.button>
            </div>

            {/* Inviting text for Upwork collaboration */}
            <div className={styles.upworkInvitation}>
              <h3 className={styles.invitationTitle}>{t('contact.upworkTitle')}</h3>
              <p className={styles.invitationText}>{t('contact.upworkDescription')}</p>
            </div>

            {/* Primary CTA - Upwork (moved to bottom for symmetry) */}
            <motion.button
              onClick={handleUpwork}
              className={`${styles.ctaButton} ${styles.shinyUpworkButton}`}
            >
              <FiUser className={styles.icon} />
              <span className={styles.buttonText}>{t('contact.methodUpwork')}</span>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ContactScreen;